import React, { useCallback, useEffect, useId, useState } from "react";

import { InputWrapper, SingleSelectInput, TitleLabel } from "@performyard/py-components";
import type { Option } from "@performyard/py-components/src/types/Option";

import { getRoleOptions } from "../../../api/services/defs/roles";

// Page size for server-side role search.
const ROLE_PAGE_SIZE = 100;

export type RoleValue = {
	id: string;
	name: string;
};

type RolePickerProps = {
	value?: RoleValue | null;
	onChange: (value: RoleValue | null) => void;
	label?: string;
	placeholder?: string;
	isClearable?: boolean;
	disabled?: boolean;
	titleImage?: string;
	titleHref?: string;
	titleTooltip?: string;
};

export const Single = ({
	value,
	onChange,
	label,
	placeholder = "Select a role...",
	isClearable = false,
	disabled = false,
	titleImage,
	titleHref,
	titleTooltip,
}: RolePickerProps) => {
	const inputId = useId();
	const [selectedRole, setSelectedRole] = useState<RoleValue | undefined>(value ?? undefined);

	// Mirror the controlled `value` so the trigger keeps the selection after a refetch.
	useEffect(() => {
		if (!value) {
			setSelectedRole(undefined);
			return;
		}
		if (selectedRole?.id === value.id) {
			return;
		}
		setSelectedRole(value);
	}, [value, selectedRole]);

	// fetchOptions (vs a static options list) keeps the search box mounted at every result count.
	const fetchRoleOptions = useCallback(async (searchTerm: string): Promise<Option<RoleValue>[]> => {
		try {
			const { roles } = await getRoleOptions({
				limit: ROLE_PAGE_SIZE,
				...(searchTerm ? { name: searchTerm } : {}),
			});
			return (roles ?? []).map((role) => ({
				label: role.name,
				value: { id: role._id, name: role.name } as RoleValue,
			}));
		} catch (error) {
			console.error("Error fetching roles:", error);
			return [];
		}
	}, []);

	const handleChange = (newValue: RoleValue | null) => {
		setSelectedRole(newValue ?? undefined);
		onChange(newValue);
	};

	const input = (
		<SingleSelectInput<RoleValue>
			fetchOptions={fetchRoleOptions}
			value={selectedRole}
			onChange={handleChange}
			placeholder={placeholder}
			isClearable={isClearable}
			disabled={disabled}
			selectionVariant="combobox"
			data-testid="role-picker"
		/>
	);

	if (label) {
		return (
			<InputWrapper
				id={inputId}
				label={
					titleHref || titleImage ? (
						<TitleLabel
							label={label}
							titleHref={titleHref}
							titleTooltip={titleTooltip}
							titleImage={titleImage}
						/>
					) : (
						label
					)
				}
				disabled={disabled}
			>
				{input}
			</InputWrapper>
		);
	}
	return input;
};

Single.displayName = "SingleSelectRole";
